import { css } from "@emotion/react";
import { FC } from "react";
import { useSelector } from "react-redux";

import { Check } from "@livechat/design-system-icons";
import { DesignToken, Heading, Icon, ProgressBar, SpacingToken, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { SubscriptionPhase } from "constants/subscription-phase";
import { selectSubscriptionPhase } from "store/entities/subscriptions";
import { withVar } from "themes/helpers";

import { IStepItemProps, StepItem } from "./StepItem";

export enum OnboardingStep {
  InstallHelpDesk = "install-helpdesk",
  ImportData = "import-data",
  ForwardEmails = "forward-emails",
  SetUpDomain = "set-up-domain",
  AllowLeavingTickets = "allow-leaving-tickets",
  InviteTeammates = "invite-teammates",
}

export type OnboardingStepItem = Omit<IStepItemProps, "order"> & { id: string };

export interface IProps {
  stepsCompleted: number;
  stepItems: OnboardingStepItem[];
}

export const OnboardingChecklist: FC<IProps> = ({ stepsCompleted, stepItems }) => {
  const isTrialWithCC = useSelector(selectSubscriptionPhase) === SubscriptionPhase.TrialCC;
  const stepsToComplete = stepItems.length;
  const progressPercentage = (stepsCompleted / stepsToComplete) * 100;
  const onboardingCompleted = stepsCompleted === stepsToComplete;

  if (onboardingCompleted) {
    if (isTrialWithCC) return null;

    return (
      <Flex column alignItems="center" justifyContent="center" height="336px" gap={withVar(SpacingToken.Spacing3)}>
        <Flex
          alignItems="center"
          justifyContent="center"
          width="64px"
          height="64px"
          borderRadius="50%"
          css={css``}
          bg={withVar(DesignToken.DecorGreen100)}
        >
          <Icon source={Check} size="xlarge" customColor={withVar(DesignToken.DecorGreen400)} />
        </Flex>
        <Heading size="sm">Congrats! All steps completed.</Heading>
      </Flex>
    );
  }

  return (
    <Flex column>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        gap={withVar(SpacingToken.Spacing3)}
        paddingBottom={withVar(SpacingToken.Spacing5)}
      >
        <Heading size="sm">Finish your setup</Heading>
        <Text
          color={withVar(DesignToken.ContentBasicSecondary)}
          css={css`
            margin-left: auto;
          `}
        >
          {stepsCompleted} of {stepsToComplete} completed
        </Text>
        <ProgressBar
          percent={progressPercentage}
          status="success"
          size="large"
          css={css`
            width: 150px;
          `}
        />
      </Flex>
      <Flex column gap={withVar(SpacingToken.Spacing3)}>
        {stepItems.map(({ id, title, completed, onClick, href, target }, index) => (
          <StepItem
            key={id}
            title={title}
            completed={completed}
            onClick={onClick}
            href={href}
            target={target}
            order={index + 1}
          />
        ))}
      </Flex>
    </Flex>
  );
};
